/**
 * fetchAdminFeedback — REST call for GET /admin/feedback, the admin
 * triage list (specs/open/feedback-submissions-management.md §1/§3/§9.2).
 *
 * AdminFeedbackItem is a frontend-local copy of SodaBrainFargateAgent's
 * feedback-store.ts type of the same name -- there is no shared types
 * package between these two repos, so the two declarations must be kept
 * in sync by hand if the backend's shape changes. Deliberately never
 * includes userAlias (the backend never sends it either, per that type's
 * own doc comment: "do not show user details").
 *
 * (§9.3) question/answer/referencedFiles are optional -- absent on any
 * item written before this field was added (the backfill gap §9.3
 * explicitly accepts), never a signal that the item itself is malformed.
 */
import { authService } from './auth';
import { API_URL } from './common/constants';
import { fetchJson } from './fetchJson';

export type FeedbackRating = 'up' | 'down';

/** Valid sinceDays window values (§9.2) -- must match feedback-store.ts's VALID_SINCE_DAYS exactly. */
export const VALID_SINCE_DAYS = [7, 14, 30, 60] as const;
export type SinceDays = (typeof VALID_SINCE_DAYS)[number];

export interface AdminFeedbackItem {
  turnId: string;
  conversationId: string;
  rating: FeedbackRating;
  comment?: string;
  createdAt: string;
  ticketId?: string;
  ticketCreatedAt?: string;
  question?: string;
  answer?: string;
  referencedFiles?: string[];
}

interface AdminFeedbackResponse {
  feedback: AdminFeedbackItem[];
  skippedCount: number;
}

/**
 * Fetch feedback items within the last `sinceDays` days. Throws if
 * there's no valid token or the request fails (including a 400 if the
 * backend rejects `sinceDays` -- this function does not itself validate
 * the value against VALID_SINCE_DAYS, since the caller (FeedbackManagement)
 * only ever offers the four fixed options via its own UI control).
 */
export async function fetchAdminFeedback(sinceDays: SinceDays): Promise<AdminFeedbackResponse> {
  return fetchJson<AdminFeedbackResponse>(
    `${API_URL}/admin/feedback?sinceDays=${sinceDays}`,
    authService.getIdToken(),
    'admin feedback',
  );
}
